import type { GraphQLError } from 'graphql';
import { useRouter } from 'next/navigation';
import { getApolloClient } from '@frameio/graphql-client';
import {
  AssetDeleted,
  type DeleteAssetsMutation,
  useDeleteAssetsMutation,
} from '@frameio/graphql-client/generated';
import { useDeselectAssets } from '@frameio/selection';

type Errors = string[] | readonly GraphQLError[];

type SyncDeleteAssetsProps = {
  assetIds: string[];
  parentId: string;
  projectId: string;
  redirectTo?: string;
  onSuccess?: (parentId: string, assetIds: string[]) => void;
  onError?: (errors: Errors) => void;
};

/**
 * useSyncDeleteAssets is used to delete smaller batches of assets. On the BE, sync deletes
 * are processed within the lifespan of a mutation request, allowing us to take advantage of
 * optimistic updates in Apollo Client.
 **/
export const useSyncDeleteAssets = () => {
  const client = getApolloClient();
  const router = useRouter();
  const [deleteAssets] = useDeleteAssetsMutation();
  const deselectIds = useDeselectAssets();

  return async ({
    assetIds,
    onSuccess,
    projectId,
    parentId,
    redirectTo,
    onError,
  }: SyncDeleteAssetsProps) => {
    const handleError = (errors: Errors) => {
      onError?.(errors);
    };

    const handleResolve = (deletedAssetIds: string[]) => {
      deselectIds(deletedAssetIds);

      if (typeof redirectTo === 'string') {
        router.push(redirectTo);
      }

      onSuccess?.(parentId, deletedAssetIds);
    };

    try {
      const response = await deleteAssets({
        optimisticResponse: {
          __typename: 'Mutation',
          deleteAssets: {
            __typename: 'DeleteAssetsPayload',
            errors: [],
            successes: assetIds.map((assetId) => {
              const asset = client.cache.readFragment({
                fragment: AssetDeleted,
                fragmentName: 'AssetDeleted',
                id: client.cache.identify({
                  __typename: 'Asset',
                  id: assetId,
                }),
              });

              if (asset?.parent?.__typename === 'VersionStackAsset') {
                return {
                  __typename: 'VersionStackAsset',
                  id: asset.parent.id,
                  deletedAt: asset.deletedAt,
                  parent: {
                    __typename: 'FolderAsset',
                    id: parentId,
                  },
                  project: {
                    __typename: 'Project',
                    id: projectId,
                  },
                  versions: [
                    {
                      __typename: asset.__typename,
                      id: asset.id,
                    },
                  ],
                };
              }

              return {
                __typename: asset?.__typename ?? 'UnsupportedAsset',
                id: assetId,
                deletedAt: asset?.deletedAt,
                parent: {
                  __typename: 'FolderAsset',
                  id: parentId,
                },
                project: {
                  __typename: 'Project',
                  id: projectId,
                },
              };
            }),
          },
        },
        variables: {
          assetIds,
        },
      });

      if (response.errors || response.data?.deleteAssets.errors?.length) {
        const errors = response.errors || response.data?.deleteAssets.errors;
        handleError(errors as Errors);
        return;
      }

      handleResolve(getAllDeletedAssetIds(response.data));
    } catch (error) {
      onError?.(error as GraphQLError[]);
    }
  };
};

/**
 * Helper function to collect all individual asset ids affected by the
 * deleteAssets operation. Handles unrolling of version stacked assets.
 *
 * @param data The returned deleted mutation data
 * @returns An flat array of all afffected assetIds
 */
function getAllDeletedAssetIds(data?: DeleteAssetsMutation | null): string[] {
  const deletedAssetIds: string[] = [];

  data?.deleteAssets.successes?.forEach((asset) => {
    /**
     * Include the ids for all the versions in a stack in the array of
     * deleted asset ids. This is useful for canceling uploads when the
     * uploading assets are in a version stack that has been deleted.
     */
    if (asset.__typename === 'VersionStackAsset') {
      const versions = asset.versions ?? [];

      deletedAssetIds.push(...versions.map(({ id }) => id));
    }

    deletedAssetIds.push(asset.id);
  });

  return deletedAssetIds;
}
